import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useSession } from "@clerk/clerk-react";
import { BadgeToggle } from "components/badgeToggle/badgeToggle.tsx";
import { Button, Accordion, AccordionItem, Input } from "@heroui/react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import {
    AlertInfo,
    Building,
    Checkmark,
    Document,
    Education,
    Link,
    User,
} from "assets/icons";
import SearchComponent from "../searchBar/components/searchComponent";
import { useTranslation } from "react-i18next";
import { formatDate } from "shared/utils/get-dates";

export const FillDocumentTemplate = () => {
    const [activeDocument, setActiveDocument] = useState(null);
    const [documentTemplates, setDocumentTemplates] = useState<any[]>([]);
    const { session } = useSession();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [tagObjects, setTagObjects] = useState<any>({});
    const [freeFields, setFreeFields] = useState<any>({});
    const { t } = useTranslation();

    useEffect(() => {
        Object.entries(tagObjects).forEach(([parentKey, entity]: any) => {
            if (entity.instances && entity.instances.length === 1) {
                const instanceId = entity.instances[0].id;
                // Automatically handle the selection
                handleRelationSelect(entity, parentKey, instanceId);
            }
        });
    }, [tagObjects]); // Depend on tagObjects so it runs when tagObjects changes
    
    // State to store field values
    const [fieldValues, setFieldValues] = useState<any>({});

    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const response = await fetchWithAuth(
                    `${API_URL}/documents/templates`,
                    { method: "GET" },
                    session,
                );
                const data = await response.json();
                setDocumentTemplates(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error(err);
            }
        };
        fetchTemplates();
    }, []);

    const groupedDocuments = documentTemplates.reduce((acc, template) => {
        const label = (template && template.label) || "Algemeen";
        (acc[label] = acc[label] || []).push(template);
        return acc;
    }, {});

    const handleDocumentSelect = async (documentId: any) => {
        try {
            const response = await fetchWithAuth(
                `${API_URL}/documents/${documentId}/tags`,
                { method: "GET" },
                session,
            );
            const data = await response.json();
            const { organizedTags, freeFields } = parseTags(data.tags.tags);
            setFreeFields(freeFields);

            if ('Datum_vandaag' in freeFields) {
                setFieldValues((prevValues: any) => ({
                    ...prevValues,
                    'Datum_vandaag': formatDate(new Date()), // Set default value
                }));
            }


            console.log(organizedTags);

            for (const [key, value] of Object.entries(organizedTags)) {
                await fetchEntityData(key, value);
            }
        } catch (error) {
            console.error("Error fetching tags:", error);
        }
    };

    const parseTags = (tags: any) => {
        let entities = {};
        let freeFields = {}; // Object to store free fields

        console.log(tags);

        tags.forEach((tag: any) => {
            const trimmedTag = tag.trim();
            // Check if the tag is a free field (no numeric values and no dots)
            if (!/\d|\./.test(trimmedTag)) {
                (freeFields as any)[trimmedTag] = "";
            } else {
                const parts = trimmedTag.split(".");
                let currentLevel: any = entities;

                parts.forEach((part: any, index: any) => {
                    const [entity, number] = part.split(/(\d+)/).filter(Boolean);

                    if (index === 0) {
                        // It's the main entity
                        if (!(currentLevel as any)[part]) {
                            (currentLevel as any)[part] = {
                                mainEntity: entity,
                                fields: [],
                                relations: {},
                            };
                        }
                        currentLevel = currentLevel[part];
                    } else if (number) {
                        // It's a relation
                        if (!currentLevel.relations[entity + number]) {
                            currentLevel.relations[entity + number] = {
                                mainEntity: entity,
                                fields: [],
                                relations: {},
                            };
                        }
                        currentLevel = currentLevel.relations[entity + number];
                    } else {
                        // It's a field of the current entity or relation
                        if (!currentLevel.fields.includes(part)) {
                            currentLevel.fields.push(part);
                        }
                    }
                });
            }
        });

        console.log(freeFields);

        return { organizedTags: entities, freeFields };
    };

    const extractRelationPaths = (entity: any, path = "", paths = new Set()) => {
        Object.entries(entity.relations || {}).forEach(
            ([relationKey, relationValue]) => {
                console.log(relationKey);
                const currentPath = path
                    ? `${path}.${(relationValue as any).mainEntity}`
                    : (relationValue as any).mainEntity;
                paths.add(currentPath);
                extractRelationPaths(relationValue, currentPath, paths);
            },
        );

        return Array.from(paths);
    };

    // Conditionally render an icon based on parentKey
    const iconMapping: Record<string, React.ReactNode> = {
        building: <Building className="h-[18px] w-[18px]" />,
        companies: <Education className="h-[18px] w-[18px]" />,
        contact: <User className="h-[18px] w-[18px]" />,
        default: <AlertInfo className="h-[18px] w-[18px]" />,
    };

    const renderRelationSelects = (entity: any, parentKey = "") => {
        const extractWord = (key: string) => {
            const match = key.match(/^[a-zA-Z]+/); // Match only the word part, ignoring numbers
            return match ? match[0] : "";
        };
        const getIconForParentKey = (key: string) => {
            const word = extractWord(key).toLowerCase();
            return iconMapping[word] || iconMapping.default;
        };
        const handleSearchSelect = (selectedItem: any) => {
            console.log(selectedItem);
            // Logic to handle the selection
            handleRelationSelect(entity, parentKey, selectedItem.id);
        };

        if (entity.instances && entity.instances.length > 10) {
            // Render SearchComponent for more than 10 instances
            return (
                <div
                    key={parentKey}
                    className="flex flex-col gap-2 rounded-sm bg-moss-primary/10 p-3"
                >
                    <header className="flex items-center gap-1">
                        {getIconForParentKey(parentKey)}
                        <label className="font-medium">{parentKey}</label>
                    </header>
                    <SearchComponent
                        customData={entity.instances.map((instance: any) => ({
                            ...instance,
                            name:
                                instance.name ||
                                (instance.firstName && instance.lastName
                                    ? `${instance.firstName} ${instance.lastName}`
                                    : instance.id.toString()),
                        }))}
                        onSelect={handleSearchSelect}
                    />
                </div>
            );
        } else if(entity.instances && entity.instances.length > 2 && entity.instances.length <= 10) {
            // Render select for 10 or fewer instances but more than 2
            return (
                <div
                    className="flex flex-col gap-2 bg-moss-primary/10 p-3"
                    key={parentKey}
                >
                    <header className="flex items-center gap-1">
                        {getIconForParentKey(parentKey)}
                        <label className="font-medium">{parentKey}</label>
                    </header>
                    <select
                        className="grow rounded-sm bg-white px-1 py-2 text-sm text-neutral-primary shadow-form-element data-[hover=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:bg-white group-data-[focus=true]:shadow-form-element-fc"
                        onChange={(e) =>
                            handleRelationSelect(entity, parentKey, e.target.value)
                        }
                    >
                        <option value="">Maak een keuze...</option>
                        {entity.instances.map((instance: any) => (
                            <option key={instance.id} value={instance.id}>
                                {instance.name ||
                                    (instance.firstName && instance.lastName
                                        ? `${instance.firstName} ${instance.lastName}`
                                        : instance.id)}
                            </option>
                        ))}
                    </select>
                </div>
            );
        }
    };

    const renderFieldInputs = (entity: any, parentKey = "") => {
        console.log(entity);
        const fields = entity.fields || [];
        const fieldInputs = fields.map((field: any) => (
            <div key={field} className="flex flex-col gap-2 p-3">
                <label className="text-sm">{t(field)}</label>
                <Input
                    value={(fieldValues as any)[`${parentKey}.${field}`] || ""}
                    onChange={(e) =>
                        handleFieldValueChange(`${parentKey}.${field}`, e.target.value)
                    }
                    classNames={{
                        input: "text-neutral-primary placeholder:text-neutral-secondary-mid",
                        inputWrapper:
                            "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
                    }}
                />
            </div>
        ));

        return [...fieldInputs];
    };

    const fetchEntityData = async (entityKey: any, entityValue: any) => {
        // Prevent redundant fetch
        if (tagObjects[entityKey]?.instances) {
            return;
        }
    
        // Extract relation paths for the given entity
        const relationPaths = extractRelationPaths(entityValue);
    
        try {
            const response = await fetchWithAuth(
                `${API_URL}/${entityValue.mainEntity}/dynamic`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ relations: relationPaths }),
                },
                session,
            );
    
            let data = await response.json();
            console.log("Received dynamic data for entity:", data);
    
            // Check if data is an object, if so convert to array
            if (!Array.isArray(data) && typeof data === 'object') {
                data = [data]; // Convert to array
            }
    
            setTagObjects((prevState: any) => ({
                ...prevState,
                [entityKey]: {
                    ...entityValue,
                    instances: data, // Set instances to the fetched data (now always an array)
                    fields: entityValue.fields, // Keep existing fields
                    relations: entityValue.relations, // Keep existing relations
                },
            }));
        } catch (error) {
            console.error("Error fetching entity data:", error);
            toast.error('Foutieve template tag in het document: "' + entityValue.mainEntity + '"', { delay: 2000 });
        }
    };
    

    const handleFieldValueChange = (tag: any, value: any) => {
        setFieldValues((prev: any) => ({ ...prev, [tag]: value }));
    };

    const findSelectedInstance = (instances: any, instanceId: any, path: any) => {
        console.log(instances);
        console.log(instanceId);
        console.log(path);
        for (const instance of instances) {
            console.log(instance);
            if (instance.id.toString() === instanceId.toString()) {
                console.log("Instance found!");
                return { instance, path };
            }
            // Recursively search in relations
            const relations = instance.relations || {};
            for (const [relKey, relValue] of Object.entries(relations)) {
                console.log(relKey);
                const found: any = findSelectedInstance(
                    (relValue as any).instances || [],
                    instanceId,
                    path.concat(relKey),
                );
                console.log("Selected instance found:")
                console.log(found);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    };

    const handleRelationSelect = async (
        entity: any,
        relationKey: any,
        instanceId: any,
    ) => {
        // Existing code to set selected instances
        console.log(relationKey);
        console.log(instanceId);

        console.log(entity);
        // Find the entity key and selected instance data
        console.log("aaaaa");
        const entityKey = relationKey.split(".")[0];
        const selected = findSelectedInstance(entity.instances, instanceId, [
            entityKey,
        ]);
        console.log("bbbbbb");

        console.log(`Selected instance for ${relationKey}:`, selected);

        // Map the fields of the selected instance to the input fields
        if (selected) {
            const { instance, path } = selected;
            console.log("ccccc");

            console.log(path);
            console.log(`Selected instance for ${path.join(".")}:`, instance);

            // Assuming each field key in tagObjects corresponds to an input field
            const entityFields = entity.fields || [];
            const inputFieldsToUpdate = {};

            entityFields.forEach((field: any) => {
                console.log(field);
                console.log(instance);
                if (instance.hasOwnProperty(field)) {
                    console.log("Field found!");
                    console.log(inputFieldsToUpdate);
                    (inputFieldsToUpdate as any)[`${relationKey}.${field}`] =
                        instance[field];
                }
            });

            // Update field values for entity-level fields
            setFieldValues((prev: any) => ({ ...prev, ...inputFieldsToUpdate }));

            // Handle relations
            const relations = entity.relations;
            console.log(`Found relations for ${entityKey}:`, relations);
            Object.entries(relations).forEach(([relKey, relValue]: any) => {
                console.log("relKey: " + relKey);
                console.log("relValue: " + relValue);
                if (instance[relValue.mainEntity]) {
                    // Check if it's a single object and not an array, then convert it to an array
                    let instances = instance[relValue.mainEntity];
                    if (!Array.isArray(instances) && typeof instances === 'object') {
                        instances = [instances]; // Convert to array
                    }
                
                    setTagObjects((prevState: any) => ({
                        ...prevState,
                        [`${relationKey}.${relKey}`]: {
                            ...relValue,
                            instances: instances, // Now instances is always an array
                        },
                    }));
                }
                
            });
        } else {
            console.log(`No data found for selected instance in ${relationKey}`);
        }
    };

    const handleTemplateToggle = (template: any) => {
        setActiveDocument(template.fileId);
        handleDocumentSelect(template.fileId);
    };

    const sendDataToBackend = async () => {
        setIsSubmitting(true);
        if (!activeDocument) {
            toast.warning(
                "Kies eerst een document / template vooraleer je verder gaat!",
            );
            setIsSubmitting(false);
            return;
        }

        console.log(fieldValues);

        const payload = {
            documentId: activeDocument,
            fields: fieldValues,
            user: session?.user,
        };

        console.log(payload);

        try {
            const response = await fetchWithAuth(
                `${API_URL}/documents/generate`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                },
                session,
            );

            if (!response.ok) throw new Error("Failed to generate document");
            const data = await response.json();
            toast.success(data.message);
        } catch (err: any) {
            toast.error(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex flex-col gap-10">
            <section className="flex flex-col gap-6">
                <header className="flex items-center gap-1 text-sm font-medium">
                    <Document className="h-[18px] w-[18px]" />
                    <p>Kies je document</p>
                </header>
                <Accordion showDivider={false} isCompact={true} variant="splitted">
                    {Object.keys(groupedDocuments).map((label) => (
                        <AccordionItem key={label} title={label} className="p-3">
                            <div className="grid grid-cols-4 gap-3">
                                {(groupedDocuments[label] || []).map((document: any) => (
                                    <div key={document.fileId}>
                                        <BadgeToggle
                                            isActive={activeDocument === document.fileId}
                                            onToggleChange={() => handleTemplateToggle(document)}
                                            icon={<Document className="h-[18px] w-[18px]" />}
                                            label={document.fileName}
                                        />
                                    </div>
                                ))}
                            </div>
                        </AccordionItem>
                    ))}
                </Accordion>
            </section>
            <section className="flex flex-col gap-6">
                <header className="flex items-center gap-1 text-sm font-medium">
                    <Link className="h-[18px] w-[18px]" />
                    <p>Maak een verbinding met de nodige entiteiten</p>
                </header>
                <div className="flex flex-col gap-6">
                    {Object.entries(tagObjects).map(([entityKey]) => (
                        <div
                            key={entityKey}
                            className="flex flex-col divide-y divide-neutral-primary/20 rounded-md border border-neutral-primary/20"
                        >
                            {/* Pass the entire entity object from tagObjects */}
                            {renderRelationSelects(tagObjects[entityKey], entityKey)}
                            {renderFieldInputs(tagObjects[entityKey], entityKey)}
                        </div>
                    ))}
                </div>

                {freeFields && Object.keys(freeFields).length > 0 && (
                    <div className="flex flex-col gap-3">
                        <p className="text-sm font-medium">Vrije velden</p>
                        {Object.keys(freeFields).map((fieldName) => {
                            const rawFieldValue = fieldValues[fieldName] || "";

                            // Check for special select dropdown format
                            if ((fieldName.includes(': [') || fieldName.includes(':[')) && fieldName.includes(']')) {
                                const [label, optionsString] = fieldName.includes(': [') ? fieldName.split(': [') : fieldName.split(':[');
                                const options = optionsString.slice(0, -1).split(','); // Extract options from the string

                                
                                return (
                                    <div key={fieldName} className="flex flex-col gap-1">
                                        <label className="text-sm">{label.trim()}</label>
                                        <select
                                            value={fieldValues[fieldName] || ""}
                                            onChange={(e) =>
                                                handleFieldValueChange(fieldName, e.target.value)
                                            }
                                            className="text-neutral-primary placeholder:text-neutral-secondary-mid p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc"
                                        >
                                            <option value="">Kies een optie...</option>
                                            {options.map((option: string) => (
                                                <option key={option.trim()} value={option.trim()}>{option.trim()}</option>
                                            ))}
                                        </select>
                                    </div>
                                );
                            } else {
                                // Render a normal input field for other free fields
                                return (
                                    <div key={fieldName} className="flex flex-col gap-1">
                                        <label className="text-sm">{fieldName}</label>
                                        <Input
                                            value={rawFieldValue}
                                            onChange={(e) =>
                                                handleFieldValueChange(fieldName, e.target.value)
                                            }
                                            classNames={{
                                                input: "text-neutral-primary placeholder:text-neutral-secondary-mid pt-2.5",
                                                inputWrapper: "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
                                            }}
                                        />
                                    </div>
                                );
                            }
                        })}
                    </div>
                )}

                {/* For debugging: console log the current state of tagObjects */}
                {console.log("Current tagObjects:", tagObjects)}
            </section>
            <Button
                onClick={sendDataToBackend}
                disabled={isSubmitting}
                startContent={<Checkmark className="h-[18px] w-[18px]" />}
                className="flex gap-1 border-1 border-moss-primary bg-moss-primary font-medium text-white"
            >
                {isSubmitting ? "Genereren..." : "Genereer"}
            </Button>
        </div>
    );
};
